import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Bug, Star, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useFeedback, getRecentConsoleErrors } from '@/hooks/useFeedbackPrompts';

function StarRow({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="p-1 rounded hover:bg-slate-100 transition"
          data-testid={`${testid}-star-${n}`}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star className={`w-6 h-6 ${n <= value ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-slate-300'}`} />
        </button>
      ))}
    </div>
  );
}

/** Bottom-left floating pill. Opens a modal with Feedback + Bug Report tabs. */
export default function FeedbackWidget() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { submit } = useFeedback();
  const es = language === 'es';
  const tt = (en, esT) => (es ? esT : en);

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('feedback');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  const reset = () => { setRating(0); setComment(''); setSeverity('medium'); setTab('feedback'); };

  const send = async () => {
    if (tab === 'feedback' && !rating) return;
    if (tab === 'bug' && !comment.trim()) return;
    setSubmitting(true);
    const ok = await submit({
      type: tab === 'bug' ? 'bug' : 'feedback',
      rating: tab === 'feedback' ? rating : null,
      severity: tab === 'bug' ? severity : null,
      comment,
      meta: { recent_console_errors: getRecentConsoleErrors() },
    });
    setSubmitting(false);
    if (ok) { setOpen(false); reset(); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-4 left-4 h-11 pl-3 pr-4 flex items-center gap-2 rounded-full bg-[#0B192C] hover:bg-[#142944] text-[#D4AF37] shadow-lg border border-[#D4AF37]/30 transition"
        data-testid="feedback-widget-btn"
        aria-label={tt('Send feedback', 'Enviar comentarios')}
      >
        <MessageCircle className="w-4 h-4" />
        <span className="text-sm font-medium">{tt('Feedback', 'Comentarios')}</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
        <DialogContent className="sm:max-w-lg" data-testid="feedback-widget-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0B192C]">
              {tt('Help us improve WakaCasa', 'Ayúdanos a mejorar WakaCasa')}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-slate-100 p-1 rounded-lg w-full">
              <TabsTrigger value="feedback" className="flex-1 rounded-md gap-1.5" data-testid="feedback-tab-feedback">
                <MessageCircle className="w-3.5 h-3.5" />{tt('Feedback', 'Comentarios')}
              </TabsTrigger>
              <TabsTrigger value="bug" className="flex-1 rounded-md gap-1.5" data-testid="feedback-tab-bug">
                <Bug className="w-3.5 h-3.5" />{tt('Bug report', 'Reportar error')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="feedback" className="space-y-3 pt-3">
              <div>
                <p className="text-xs text-slate-500 mb-2">{tt('How was your experience?', '¿Qué tal tu experiencia?')}</p>
                <StarRow value={rating} onChange={setRating} testid="feedback-rating" />
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">{tt('Tell us more (optional)', 'Cuéntanos más (opcional)')}</p>
                <Textarea value={comment} onChange={e => setComment(e.target.value)} rows={4}
                  placeholder={tt('Your thoughts…', 'Tus comentarios…')} data-testid="feedback-comment" />
              </div>
            </TabsContent>

            <TabsContent value="bug" className="space-y-3 pt-3">
              <div>
                <p className="text-xs text-slate-500 mb-1">{tt('Severity', 'Gravedad')}</p>
                <select value={severity} onChange={e => setSeverity(e.target.value)}
                  className="w-full h-10 border border-slate-200 rounded-md bg-white px-3 text-sm"
                  data-testid="feedback-severity">
                  <option value="low">{tt('Low — minor annoyance', 'Baja — molestia menor')}</option>
                  <option value="medium">{tt('Medium — impacts my workflow', 'Media — afecta mi flujo')}</option>
                  <option value="high">{tt('High — feature is unusable', 'Alta — la función no sirve')}</option>
                  <option value="critical">{tt('Critical — I cannot use the app', 'Crítica — no puedo usar la app')}</option>
                </select>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">{tt('What happened?', '¿Qué pasó?')} *</p>
                <Textarea value={comment} onChange={e => setComment(e.target.value)} rows={5}
                  placeholder={tt('Steps to reproduce, what you expected vs what happened…', 'Pasos, qué esperabas vs qué ocurrió…')}
                  data-testid="feedback-bug-comment" />
                <p className="text-[10px] text-slate-400 mt-1">
                  {tt('We automatically attach: current URL, your browser, viewport size, and recent console errors.',
                      'Adjuntamos automáticamente: URL, navegador, tamaño de pantalla y errores recientes de consola.')}
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setOpen(false)}>{tt('Cancel', 'Cancelar')}</Button>
            <Button onClick={send}
              disabled={submitting || (tab === 'feedback' && !rating) || (tab === 'bug' && !comment.trim())}
              className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold gap-1.5"
              data-testid="feedback-submit-btn">
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {tt('Send', 'Enviar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Slide-in card for event-triggered prompts. */
export function FeedbackPromptCard() {
  const { language } = useLanguage();
  const es = language === 'es';
  const tt = (en, esT) => (es ? esT : en);
  const { activePrompt, answerPrompt, dismissPrompt } = useFeedback();

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!activePrompt) return null;

  const send = async () => {
    if (!rating) return;
    setSubmitting(true);
    await answerPrompt({ rating, comment });
    setSubmitting(false);
    setRating(0); setComment('');
  };

  const skip = async () => { setRating(0); setComment(''); await dismissPrompt(); };
  const title = es ? activePrompt.prompt_es : activePrompt.prompt_en;

  return (
    <div
      className="fixed z-40 bottom-4 left-20 sm:left-24 max-w-[calc(100vw-32px)] sm:max-w-sm w-96 bg-white border border-[#D4AF37]/40 rounded-xl shadow-xl p-4"
      data-testid="feedback-prompt-card"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-[#0B192C] flex-1" data-testid="feedback-prompt-title">{title}</p>
        <button type="button" onClick={skip} className="text-slate-400 hover:text-slate-600 text-lg leading-none"
          data-testid="feedback-prompt-skip" aria-label="Skip">×</button>
      </div>
      <div className="mt-3 flex items-center justify-center">
        <StarRow value={rating} onChange={setRating} testid="feedback-prompt-rating" />
      </div>
      {rating > 0 && (
        <>
          <Textarea value={comment} onChange={e => setComment(e.target.value)} rows={2}
            placeholder={tt('Optional — tell us more', 'Opcional — cuéntanos más')}
            className="mt-3 text-sm" data-testid="feedback-prompt-comment" />
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={skip}>{tt('Skip', 'Omitir')}</Button>
            <Button size="sm" onClick={send} disabled={submitting}
              className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold"
              data-testid="feedback-prompt-send">
              {submitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {tt('Send', 'Enviar')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
